import type { Layout } from "react-grid-layout";

/** The dashboard grid is a fixed 4-column canvas (matches the v1 backend). */
export const GRID_COLS = 4;

/**
 * Minimal shape the grid packer needs from a widget. Both the metrics report
 * widgets and the Home dashboard widgets map onto this interface, so the layout
 * math can stay agnostic of the concrete widget model.
 */
export interface GridWidget {
  widget_key: string;
  column_span: number;
  row_span: number;
  /** Minimum row span the widget may shrink to. Defaults to 2. */
  min_row_span?: number;
}

type LayoutItem = Record<string, unknown> & { id?: string };

/** Layout entry id — Cloudscape used `id`; RGL uses `i`. Accept both. */
function layoutEntryId(item: LayoutItem): string | null {
  if (typeof item.id === "string") return item.id;
  if (typeof item.i === "string") return item.i;
  return null;
}

/** Build a `widget_key -> layout entry` map from the persisted layout JSON. */
export function buildLayoutMap(layout: unknown): Map<string, LayoutItem> {
  const map = new Map<string, LayoutItem>();
  if (!Array.isArray(layout)) return map;
  for (const li of layout as LayoutItem[]) {
    const id = li ? layoutEntryId(li) : null;
    if (id) map.set(id, li);
  }
  return map;
}

/**
 * Reorder widgets to match the persisted layout JSON, which is the source of
 * truth for widget order. Widgets absent from the layout keep their original
 * relative order and are appended after the positioned ones.
 */
export function orderWidgetsByLayout<T extends { widget_key: string }>(
  widgets: T[],
  layout: unknown,
): T[] {
  if (!Array.isArray(layout) || layout.length === 0) return widgets;

  const rank = new Map<string, number>();
  (layout as LayoutItem[]).forEach((li, idx) => {
    const id = li ? layoutEntryId(li) : null;
    if (id && !rank.has(id)) rank.set(id, idx);
  });
  if (rank.size === 0) return widgets;

  return widgets
    .map((w, idx) => ({ w, idx }))
    .sort((a, b) => {
      const ra = rank.get(a.w.widget_key) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b.w.widget_key) ?? Number.MAX_SAFE_INTEGER;
      return ra - rb || a.idx - b.idx;
    })
    .map(({ w }) => w);
}

/**
 * Convert widgets + persisted backend layout into a react-grid-layout `Layout[]`.
 * The backend stores Cloudscape-style entries (`columnSpan`, `rowSpan`,
 * `columnOffset`) without explicit y, so we pack rows top-to-bottom in order,
 * honoring each widget's stored column offset when present.
 */
export function widgetsToRglLayout(
  widgets: GridWidget[],
  layout: unknown,
): Layout[] {
  const ordered = orderWidgetsByLayout(widgets, layout);
  const layoutMap = buildLayoutMap(layout);
  const occupied = new Set<string>();

  const fits = (x: number, y: number, w: number, h: number): boolean => {
    for (let r = y; r < y + h; r++) {
      for (let c = x; c < x + w; c++) {
        if (occupied.has(`${r},${c}`)) return false;
      }
    }
    return true;
  };
  const occupy = (x: number, y: number, w: number, h: number) => {
    for (let r = y; r < y + h; r++) {
      for (let c = x; c < x + w; c++) occupied.add(`${r},${c}`);
    }
  };

  const result: Layout[] = [];
  for (const widget of ordered) {
    const entry = layoutMap.get(widget.widget_key);
    const minRow = widget.min_row_span ?? 2;
    const w = clamp(
      numberOr(entry?.columnSpan ?? entry?.column_span, widget.column_span || 1),
      1,
      GRID_COLS,
    );
    const h = Math.max(
      minRow,
      numberOr(entry?.rowSpan ?? entry?.row_span, widget.row_span || minRow),
    );
    const offsetMap = entry?.columnOffset as Record<string, number> | undefined;
    const desiredX =
      offsetMap && typeof offsetMap[String(GRID_COLS)] === "number"
        ? clamp(offsetMap[String(GRID_COLS)], 0, GRID_COLS - w)
        : null;

    let placedX = 0;
    let placedY = 0;
    outer: for (let y = 0; ; y++) {
      const xs = desiredX != null ? [desiredX] : range(0, GRID_COLS - w);
      for (const x of xs) {
        if (fits(x, y, w, h)) {
          placedX = x;
          placedY = y;
          break outer;
        }
      }
    }
    occupy(placedX, placedY, w, h);
    result.push({
      i: widget.widget_key,
      x: placedX,
      y: placedY,
      w,
      h,
      minW: 1,
      minH: minRow,
    });
  }
  return result;
}

/**
 * Convert a react-grid-layout `Layout[]` back into the Cloudscape-compatible
 * backend layout JSON, ordered top-to-bottom then left-to-right.
 */
export function rglLayoutToBackend(layout: Layout[]): {
  id: string;
  columnSpan: number;
  rowSpan: number;
  columnOffset: Record<string, number>;
}[] {
  return [...layout]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((item) => ({
      id: item.i,
      columnSpan: item.w,
      rowSpan: item.h,
      columnOffset: { [String(GRID_COLS)]: item.x },
    }));
}

/**
 * Reorder widgets to match a react-grid-layout `Layout[]` (top-to-bottom,
 * left-to-right), so the persisted widget order agrees with the visual layout.
 */
export function orderWidgetsByRgl<T extends { widget_key: string }>(
  widgets: T[],
  layout: Layout[],
): T[] {
  const rank = new Map<string, number>();
  [...layout]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .forEach((item, idx) => rank.set(item.i, idx));
  return [...widgets].sort((a, b) => {
    const ra = rank.get(a.widget_key) ?? Number.MAX_SAFE_INTEGER;
    const rb = rank.get(b.widget_key) ?? Number.MAX_SAFE_INTEGER;
    return ra - rb;
  });
}

type SpannedWidget = {
  widget_key: string;
  column_span: number;
  row_span: number;
};

/**
 * Copy live RGL `w`/`h` onto each widget's `column_span`/`row_span`. Resize
 * only mutates the layout array; without this the widget records stay at their
 * original spans and a refresh falls back to the stale height.
 */
export function applyRglSpansToWidgets<T extends SpannedWidget>(
  widgets: T[],
  layout: Layout[],
): T[] {
  if (widgets.length === 0 || layout.length === 0) return widgets;
  const byKey = new Map(layout.map((item) => [item.i, item]));
  let changed = false;
  const next = widgets.map((widget) => {
    const item = byKey.get(widget.widget_key);
    if (!item) return widget;
    const column_span = item.w;
    const row_span = item.h;
    if (column_span === widget.column_span && row_span === widget.row_span) {
      return widget;
    }
    changed = true;
    return { ...widget, column_span, row_span };
  });
  return changed ? next : widgets;
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

function range(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let i = lo; i <= hi; i++) out.push(i);
  return out;
}

/**
 * Display-only layout for mobile: stack every widget full-width in visual order.
 * Does not mutate persisted desktop spans — map back through the caller.
 */
export function stackLayoutForMobile(
  layout: Layout[],
  hardMinRowByKey: Record<string, number> = {},
): Layout[] {
  const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  let y = 0;
  return sorted.map((item) => {
    const hardMin = hardMinRowByKey[item.i] ?? 0;
    const h = Math.max(item.h, hardMin);
    const stacked: Layout = {
      ...item,
      x: 0,
      w: GRID_COLS,
      y,
      h,
      minH: Math.max(item.minH ?? 0, hardMin),
    };
    y += h;
    return stacked;
  });
}
